import {type SEOHandle} from '@nasa-gcn/remix-seo';
import {type LoaderFunctionArgs, type MetaFunction} from '@remix-run/node';
import {Link, useLoaderData} from '@remix-run/react';
import {type Tag} from '@tryghost/content-api';
import parse from 'html-react-parser';
import {serverOnly$} from 'vite-env-only/macros';
import {Container} from '#/app/components/Container.jsx';
import {Image} from '#/app/components/Image.jsx';
import {SubscribeForm} from '#/app/components/SubscribeForm.jsx';
import {contentApi} from '#/app/helpers/api.server.js';
import {createMetaHandler} from '#/app/helpers/createMetaHandler.js';
import {createMetaTags} from '#/app/helpers/createMetaTags.js';

export const handle: SEOHandle = {
  getSitemapEntries: serverOnly$(async (_request) => {
    const posts = await contentApi().posts.browse({filter: 'featured:false', include: ['tags', 'authors'], limit: 999});
    return posts.map((post) => ({
      priority: 0.7,
      route: `/blog/${post.slug}`,
    }));
  }),
};

export async function loader({params}: LoaderFunctionArgs) {
  const {slug = ''} = params;
  const [post, morePosts] = await Promise.all([
    contentApi().posts.read({slug}, {include: ['tags', 'authors']}),
    contentApi().posts.browse({filter: `featured:false+slug:-${slug}`, include: ['tags', 'authors'], limit: 3}),
  ]);
  return {morePosts, post};
}

export const meta: MetaFunction<typeof loader> = createMetaHandler(({data}) => {
  // @ts-expect-error
  const {post} = data;
  return createMetaTags({
    description: post.excerpt,
    image: post.feature_image,
    title: post.title,
    url: `https://ufleet.io/blog/${post.slug}`,
  });
});

export default function BlogSlug() {
  const {morePosts, post} = useLoaderData<typeof loader>();

  const postUrl = `https://ufleet.io/blog/${post.slug}`;
  const tag = post.primary_tag ?? ({accent_color: '#374151', name: 'Ufleet', slug: 'ufleet'} as Tag);
  const author = post.primary_author ?? {name: '', profile_image: '', slug: ''};

  return (
    <>
      <Container className="px-2 pb-32 pt-10 text-gray-700">
        <div className="mx-auto max-w-5xl space-y-16">
          <div className="space-y-10">
            <div className="space-y-5">
              <div className="flex space-x-2">
                <Link
                  className={`_nav flex items-center space-x-2 font-semibold uppercase hover:underline`}
                  // style={{color: tag.accent_color!}}
                  to={`/blog`}
                >
                  <svg
                    className="inline-block"
                    height="1em"
                    viewBox="0 0 448 512"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
                      fill="currentColor"
                    />
                  </svg>
                </Link>
                <Link
                  className={`_nav flex items-center space-x-2 font-semibold uppercase hover:underline`}
                  style={{color: tag.accent_color!}}
                  to={`/blog/tags/${tag.slug}`}
                >
                  <span> {tag.name}</span>
                </Link>
                {/* // TODO:
                <Button appearance="link" className="hover:underline w-full" color="pink">
                  ♥ Subscribe
                </Button> */}
              </div>
              <header>
                <h2 className="whitespace-pre-wrap leading-[1.1]">{post.title}</h2>
              </header>
              <article>
                <p className="ml-0.5 whitespace-pre-wrap text-lg text-gray-500 sm:text-2xl sm:leading-normal">{post.excerpt}</p>
              </article>
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <Image
                    height={48}
                    src={author.profile_image!}
                    width={48}
                  />
                  <div className="">
                    <div className="font-semibold text-gray-700">{author.name}</div>
                    <div className="text-gray-500">
                      {new Date(post.published_at!).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </div>
                  </div>
                </div>
                <ol className="flex">
                  <li>
                    <a
                      className="block p-2 text-gray-400 hover:text-sky-500"
                      href={`https://twitter.com/share?text=${post.title}&url=${encodeURI(postUrl)}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span>
                        <svg
                          height="26"
                          viewBox="0 0 16 13"
                          width="20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="m14.368 3.23501v.429c0 4.336-3.297 9.33599-9.329 9.33599-1.851 0-3.594-.406-5.039-1.336.258.039.516.055.782.055 1.531 0 2.96-.664 4.07-1.555-1.422-.015-2.649-.98399-3.055-2.28099.203.039.406.063.609.063.298 0 .594-.04.876-.118-.74313-.14953-1.41153-.55183-1.89155-1.1385-.480014-.58666-.742015-1.32148-.74145-2.0795v-.039c.445.242.945.39 1.484.406-.44999-.29696-.81952-.70063-1.07566-1.17504-.256134-.47441-.390919-1.00483-.39234-1.54396 0-.609.171-1.109.445-1.594004 1.633 1.985004 4.039 3.219004 6.757 3.368004-.05105-.24934-.07453-.50354-.07-.758-.00079-.43017.08335-.85627.24761-1.25384.16425-.39758.40538-.75882.70956-1.062995.30418-.304176.66541-.545307 1.06299-.709561.39754-.164254.82364-.248399 1.25384-.24760848.945 0 1.797.40599948 2.414 1.03200448.73-.147083 1.4299-.416549 2.07-.797004-.2414.763754-.7559 1.411724-1.445 1.820004.6532-.07629 1.2907-.25304 1.89-.524-.445.672-1 1.243-1.632 1.704z"
                            fill="currentColor"
                          ></path>
                        </svg>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      className="block p-2 text-gray-400 hover:text-blue-700"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURI(postUrl)}&t=${post.title}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span>
                        <svg
                          height="23"
                          viewBox="0 0 16 16"
                          width="23"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="m15.1631 0h-14.32649c-.23294.0210424-.449075.129423-.60447.303111-.1553959.173689-.23839025.39965-.23211344.631954v14.129835c-.00627681.2323.07671754.4583.23211344.632.155395.1737.37153.2821.60447.3031h7.63383v-6.23377h-2.09146v-2.38961h2.09146v-1.76623c-.06833-.7686.16587-1.53359.65339-2.13421.48753-.60061 1.19037-.99009 1.96097-1.08657h.5228c.6293-.01721 1.2589.01754 1.8823.1039v2.18181h-1.3594c-1.0457 0-1.1503.51949-1.1503 1.14286v1.55844h2.4052l-.3137 2.38961h-2.0915v6.12987h4.0783c.2339.0062.4613-.0762.6361-.2306s.2839-.3691.3051-.6006v-14.233731c-.0147-.215637-.1075-.418678-.2614-.571504-.1538-.152827-.3581-.245079-.5752-.259665z"
                            fill="currentColor"
                          ></path>
                        </svg>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a
                      className="block p-2 text-gray-400 hover:text-blue-600"
                      href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURI(postUrl)}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      <span>
                        <svg
                          height="24"
                          viewBox="0 0 16 16"
                          width="24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="m1.961 3.743c-1.18 0-1.961-.83-1.961-1.876 0-1.053.797-1.867 2.016-1.867 1.203 0 1.969.814 1.984 1.867 0 1.046-.781 1.876-2.015 1.876zm-1.625 1.7h3.328v10.557h-3.328zm8.664 4.427c0-1.364.852-2.043 1.852-2.043s1.812.679 1.812 2.387v5.786h3.336v-6.129c0-3.064-1.664-4.764-4-4.764-1.335 0-2.336.775-3 1.796l-.109-1.46h-3.281c0 .359.054 2.385.054 2.385v8.172h3.336v-6.129z"
                            fill="currentColor"
                          ></path>
                        </svg>
                      </span>
                    </a>
                  </li>
                </ol>
              </div>
            </div>
            <Image
              alt={post.feature_image_alt!}
              className="rounded-lg py-5"
              layout="fullWidth"
              src={post.feature_image!}
            />
            {/* @ts-expect-error */}
            <div className="prose mx-auto max-w-3xl lg:prose-xl">{parse(post.html!)}</div>
          </div>
        </div>
      </Container>
      <div className="w-full pb-40">
        <div className="mx-auto max-w-3xl pb-32">
          <h2 className="mb-20 w-full text-center font-semibold">Never miss a post</h2>
          <SubscribeForm />
        </div>
        <Container className="">
          <h2 className="mb-20 w-full text-center font-semibold">You may also like…</h2>
          <div className="grid w-full grid-cols-1 gap-16 sm:grid-cols-3">
            {morePosts.map((post: Record<string, any>, i: any) => {
              if (i === 'meta') {
                return null;
              }
              const tag = post.primary_tag ?? 'Ufleet';
              const author = post.primary_author ?? {name: '', profile_image: '', slug: ''};
              return (
                <div className="space-y-2" key={`${i}-${post.id}-${post.slug}`}>
                  <Link className="_nav" to={`/blog/${post.slug}`}>
                    <Image
                      alt={post.feature_image_alt!}
                      className="rounded-lg"
                      layout="fullWidth"
                      src={post.feature_image!}
                    />
                  </Link>
                  <Link
                    className={`_nav block pt-2 font-semibold uppercase hover:underline`}
                    style={{color: tag.accent_color}}
                    to={`/blog/tags/${tag.slug}`}
                  >
                    {tag.name}
                  </Link>
                  <Link className="_nav block text-2xl font-semibold text-gray-700 hover:underline" to={`/blog/${post.slug}`}>
                    {post.title}
                  </Link>
                  <div className="flex space-x-2">
                    <Image
                      height={48}
                      src={author.profile_image}
                      width={48}
                    />
                    <div className="">
                      <div className="font-semibold text-gray-700">{author.name}</div>
                      <div className="text-gray-500">
                        {new Date(post.created_at).toLocaleDateString('en-US', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </div>
    </>
  );
}
